import * as React from 'react';
import { getTranslate } from 'react-localize-redux';
import PageLoader from '../pageLoader/PageLoader';
import storeLang from '../../helpers/storeLang';
import { ImageThumbnail, ImageResult } from './model/Model';
import { AddBlock, AddImage, Container, FileInput, Icon, ImageAdd } from './style/InputImageStyle';
import { PrimaryColor } from '../../styles/global/css/Utils';
import imageCompression from 'browser-image-compression';

export enum InputErrorMode {
  LIMIT_REACHED
}

interface InputError {
  type: InputErrorMode
  message: string
}

type InputImageProps<IsProduct extends boolean=false> = {
	isProduct?: IsProduct
	onChange?: (value: IsProduct extends true ? ImageThumbnail : ImageResult) => void
	isLoading?: boolean
	onError?: (value: InputError) => void
	disableShowImage?: boolean
	limitMo?: number
	size?: number
	backgroundColor?: string,
	compress?: boolean
	maxWidthOrHeight?: number
	url?: string
}

function InputImage<IsProduct extends boolean=false>(props: InputImageProps<IsProduct>): JSX.Element {
	const translate = getTranslate(storeLang.getState().localize);
	const [image, setImage] = React.useState<string>(props.url ?? '');
	const reader = new FileReader();
	const [imageIsLoading, setImageIsLoading] = React.useState<boolean>(false);

	React.useEffect(() => {
		setImage(props.url ?? '');
	}, [props.url]);

	const hashStr = async(array: ArrayBuffer) => {
		const hashBuffer = await crypto.subtle.digest('SHA-256', array);
		const hashArray = Array.from(new Uint8Array(hashBuffer));
		return (hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''));
	};

	const size = props.size ?? 30;
	const isLoading = props.isLoading || imageIsLoading;
	const hasImage = image.length > 0;
	const accent = props.backgroundColor ?? PrimaryColor;

	return (
		<Container size={size} title={translate('images.add_image').toString()}>
			<FileInput name='image_input' type='file' accept='.jpeg, .jpg, .png' autoComplete='off' onInput={async(e) => {
				const files = e.currentTarget.files;
				if (!files || !files[0]) return;
				let uploadedFile: File = files[0];
				if (uploadedFile.type.startsWith('image')) {
					setImageIsLoading(true);
					if (props.compress) {
						try {
							uploadedFile = await imageCompression(uploadedFile, { maxSizeMB: props.limitMo ?? 1 });
						} catch (e) {
							console.error(e);
							props.onError?.({
								type: InputErrorMode.LIMIT_REACHED,
								message: e
							});
							return;
						}
					}
					if (props.limitMo && parseFloat(((uploadedFile.size / 1024) / 1024).toFixed(4)) > props.limitMo) {
						props.onError?.({
							type: InputErrorMode.LIMIT_REACHED,
							message: translate('global.fire.size_exceeded').toString().replace('{{SIZE}}', props.limitMo.toString())
						});
						return;
					}
					
					const img = new Image();
					reader.onload = async(evt) => {
						if (!evt || !evt.target || !evt.target.result || typeof evt.target.result != 'string') return;
						img.src = evt.target.result;
						img.onload = () => {
							const canvas = document.createElement('canvas');
							const scale = props.maxWidthOrHeight ? Math.min(1, props.maxWidthOrHeight / Math.max(img.width, img.height)) : 1;
							canvas.width = Math.round(img.width * scale);
							canvas.height = Math.round(img.height * scale);
							canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
							const webpDataURL = canvas.toDataURL('image/webp');
							const tab = webpDataURL.split(',');
							const contentType = tab[0];
							const contents = tab[1];
							!props.disableShowImage && setImage(webpDataURL);
							canvas.toBlob(async(blob) => {
								if (!blob) return;
								const blobArray = await blob.arrayBuffer();
								const hash = await hashStr(blobArray);
								const imageWebp = new File([blob], hash + '.webp', { type: blob.type });
								const thumbnail = await imageCompression(imageWebp, { maxWidthOrHeight: 256 });
								setImageIsLoading(false);
								if (props.onChange != null) 
								{
									if (props.isProduct)
										props.onChange({
											original: imageWebp,
											thumbnail: thumbnail,
											url: webpDataURL
										} as IsProduct extends true ? ImageThumbnail : ImageResult);
									else
										props.onChange({
											file: imageWebp,
											content: contents,
											contentType: contentType,
											url: webpDataURL
										} as IsProduct extends true ? ImageThumbnail : ImageResult);
								}
							}, 'image/webp');
						};
					};
					reader.readAsDataURL(uploadedFile);
				}
			}} />
			<AddBlock backoungroundColor={props.backgroundColor} noBackground={isLoading} hasImage={hasImage && !isLoading}>
				{isLoading
					? <PageLoader size={Math.round(size * 0.5)} color={accent} />
					: hasImage
						? <AddImage src={image} />
						: <Icon url={ImageAdd} color={accent} />
				}
			</AddBlock>
		</Container>
	);
}

export default InputImage;
